import { useState } from 'react';
import type { FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '../api/ApiProvider';
import { useApiQuery } from '../api/useApiQuery';
import type { AuditEvent, AuditQuery } from '../api/types';
import { useMe } from '../hooks/useMe';
import { formatDateTime } from '../lib/format';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Chip } from '../components/ui/Chip';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { Spinner } from '../components/ui/Spinner';
import { Breadcrumbs } from '../components/EntityHeader';
import { AccessDenied } from './AccessDenied';

function outcomeVariant(outcome: string): 'success' | 'danger' | 'warning' | 'info' {
  const o = outcome.toLowerCase();
  if (o === 'allow' || o === 'allowed' || o === 'success' || o === 'ok') return 'success';
  if (o === 'deny' || o === 'denied' || o === 'rejected') return 'danger';
  if (o === 'error' || o === 'failed' || o === 'failure') return 'warning';
  return 'info';
}

const EMPTY_FILTER: AuditQuery = { actor: '', action: '', resourceType: '', resourceId: '', from: '', to: '' };

/** The filter fields present in the query string (e.g. a file's "View activity" link). */
const URL_FILTER_KEYS = ['actor', 'action', 'resourceType', 'resourceId', 'from', 'to'] as const;

function filterFromParams(params: URLSearchParams): AuditQuery {
  const out: AuditQuery = { ...EMPTY_FILTER };
  for (const key of URL_FILTER_KEYS) {
    const value = params.get(key);
    if (value) out[key] = value;
  }
  return out;
}

export function AuditPage() {
  const { canAudit, loading } = useMe();
  const api = useApi();
  const [params] = useSearchParams();
  const [draft, setDraft] = useState<AuditQuery>(() => filterFromParams(params));
  const [filter, setFilter] = useState<AuditQuery>(() => filterFromParams(params));
  const [extra, setExtra] = useState<AuditEvent[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [moreBusy, setMoreBusy] = useState(false);

  const page = useApiQuery(
    async (client, signal) => {
      const res = await client.audit({ ...filter, limit: 50 }, signal);
      setExtra([]);
      setCursor(res.nextCursor);
      return res;
    },
    [filter],
    { enabled: canAudit },
  );

  if (loading) return <Spinner label="Checking your roles" />;
  if (!canAudit) return <AccessDenied need="Auditor or Records.Admin" />;

  const apply = (e: FormEvent) => {
    e.preventDefault();
    setFilter({ ...draft });
  };

  const loadMore = async () => {
    if (!cursor) return;
    setMoreBusy(true);
    try {
      const res = await api.audit({ ...filter, limit: 50, cursor });
      setExtra((prev) => [...prev, ...res.events]);
      setCursor(res.nextCursor);
    } finally {
      setMoreBusy(false);
    }
  };

  const events = [...(page.data?.events ?? []), ...extra];
  const field = (key: keyof AuditQuery, label: string, type = 'text') => (
    <label className="block text-xs">
      <span className="font-medium text-muted-foreground">{label}</span>
      <input
        type={type}
        className="gsl-input mt-1 !py-1.5 text-sm"
        value={(draft[key] as string | undefined) ?? ''}
        onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
      />
    </label>
  );

  return (
    <div className="space-y-4">
      <div>
        <Breadcrumbs items={[{ label: 'Audit trail' }]} />
        <h1 className="mt-2 text-4xl leading-none">Audit trail</h1>
      </div>
      <Card title="Filter">
        <form onSubmit={apply} className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
          {field('actor', 'Actor (UPN or oid)')}
          {field('action', 'Action')}
          {field('resourceType', 'Resource type')}
          {field('resourceId', 'Resource id')}
          {field('from', 'From', 'datetime-local')}
          {field('to', 'To', 'datetime-local')}
          <div className="flex items-end gap-2 md:col-span-3 lg:col-span-6">
            <Button type="submit" size="sm">
              Apply
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setDraft(EMPTY_FILTER);
                setFilter(EMPTY_FILTER);
              }}
            >
              Reset
            </Button>
          </div>
        </form>
      </Card>

      <Card title="Events" padded={false}>
        {page.loading ? (
          <div className="p-4">
            <Spinner label="Loading audit events" />
          </div>
        ) : page.error ? (
          <div className="p-4">
            <ErrorBanner title="Audit events could not be loaded" error={page.error} />
          </div>
        ) : events.length === 0 ? (
          <div className="p-4">
            <EmptyState title="No events match" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="gsl-th">When</th>
                  <th className="gsl-th">Actor</th>
                  <th className="gsl-th">Action</th>
                  <th className="gsl-th">Resource</th>
                  <th className="gsl-th">Outcome</th>
                  <th className="gsl-th">Correlation</th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev) => (
                  <tr key={ev.id} className="gsl-row border-b border-border last:border-b-0 align-top">
                    <td className="whitespace-nowrap px-4 py-2 text-xs">{formatDateTime(ev.occurredAt)}</td>
                    <td className="px-4 py-2">
                      <div>{ev.actorUpn ?? ev.actorOid}</div>
                      {ev.ip ? <div className="text-xs text-muted-foreground">{ev.ip}</div> : null}
                    </td>
                    <td className="px-4 py-2 font-medium">{ev.action}</td>
                    <td className="px-4 py-2">
                      <div>{ev.resourceType}</div>
                      {ev.resourceId ? <div className="gsl-num text-xs text-muted-foreground">{ev.resourceId}</div> : null}
                    </td>
                    <td className="px-4 py-2">
                      <Chip variant={outcomeVariant(ev.outcome)}>{ev.outcome}</Chip>
                      {ev.details && Object.keys(ev.details).length > 0 ? (
                        <details className="mt-1 text-xs">
                          <summary className="cursor-pointer text-muted-foreground">details</summary>
                          <pre className="mt-1 max-w-xs overflow-x-auto whitespace-pre-wrap break-all">{JSON.stringify(ev.details, null, 1)}</pre>
                        </details>
                      ) : null}
                    </td>
                    <td className="gsl-num px-4 py-2 text-xs text-muted-foreground">{ev.correlationId ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {cursor ? (
              <div className="border-t border-border p-3 text-center">
                <Button variant="secondary" size="sm" busy={moreBusy} onClick={() => void loadMore()}>
                  Load more
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </Card>
    </div>
  );
}
